import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Loader2, List, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';
import { getArticleBySlug, getStrapiMediaUrl, formatDate, extractHeadings, renderBlocksToHtml } from '../lib/strapi';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchArticle() {
      setLoading(true);
      setNotFound(false);
      const data = await getArticleBySlug(slug);
      if (data) {
        setArticle(data);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  // Dynamic meta tags
  usePageMeta({
    title: article ? `${article.title} - Wricoo Blog` : 'Loading... - Wricoo Blog',
    description: article?.description || 'Read this article on Wricoo Blog.',
    robots: 'index, follow',
    canonical: `https://wricoo.com/blog/${slug}/`,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="blog-post-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading article...</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white" data-testid="blog-post-not-found">
        <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
            <Link to="/blog/" className="text-blue-200 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/blog/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const coverImage = article.cover ? getStrapiMediaUrl(article.cover) : null;
  const author = article.author;
  const category = article.category;
  const blocks = article.blocks || [];
  const headings = extractHeadings(blocks);
  const contentHtml = renderBlocksToHtml(blocks);

  // Generate BlogPosting Schema
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.description || '',
    "image": coverImage || '',
    "datePublished": article.publishedAt,
    "dateModified": article.updatedAt || article.publishedAt,
    "author": author ? {
      "@type": "Person",
      "name": author.name
    } : {
      "@type": "Organization",
      "name": "Wricoo"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Wricoo",
      "logo": {
        "@type": "ImageObject",
        "url": "https://wricoo.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://wricoo.com/blog/${slug}/`
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }} />

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/blog/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
            <Link to="/blog/" className="text-sm font-medium text-white transition">Blog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Feature Image - High Priority */}
      {coverImage && (
        <div className="w-full bg-gray-100">
          <img 
            src={coverImage} 
            alt={article.title} 
            className="w-full h-64 sm:h-80 lg:h-96 object-cover"
            fetchpriority="high"
          />
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <a href="/" className="hover:text-blue-600">Home</a>
          <ChevronRight className="w-4 h-4 mx-2" />
          <a href="/blog/" className="hover:text-blue-600">Blog</a>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 truncate max-w-xs">{article.title}</span>
        </nav>

        {/* Category Badge */}
        {category && (
          <span className="inline-block text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full mb-4">
            {category.name}
          </span>
        )}

        {/* Title (H1) */}
        <h1 className="font-bold text-gray-900 leading-tight mb-6">{article.title}</h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-gray-500 mb-8 pb-8 border-b border-gray-200">
          {author && (
            <div className="flex items-center gap-2">
              {author.avatar ? (
                <img 
                  src={getStrapiMediaUrl(author.avatar)} 
                  alt={author.name} 
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
              )}
              <span className="font-medium text-gray-700">{author.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(article.publishedAt)}</span>
          </div>
        </div>

        {/* Table of Contents */}
        {headings.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8" data-testid="table-of-contents">
            <div className="flex items-center gap-2 mb-3">
              <List className="w-5 h-5 text-blue-600" />
              <h2 className="font-semibold text-gray-900">Table of Contents</h2>
            </div>
            <nav>
              <ul className="space-y-2">
                {headings.map((heading, index) => (
                  <li key={index} className={heading.level === 3 ? 'ml-4' : ''}>
                    <a 
                      href={`#${heading.id}`} 
                      className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}

        {/* Article Body */}
        <div 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
          data-testid="article-content"
        />

        {/* Author Bio */}
        {author && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-start gap-4 bg-gray-50 rounded-lg p-6">
              {author.avatar ? (
                <img 
                  src={getStrapiMediaUrl(author.avatar)} 
                  alt={author.name} 
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Written by {author.name}</h3>
                {author.email && (
                  <p className="text-gray-500 text-sm">{author.email}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </article>

      {/* CTA Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-4">Ready to Streamline Your Content Operations?</h2>
          <p className="text-gray-600 mb-6">Join content teams who trust Wricoo to manage their SEO briefs and content workflows.</p>
          <Button size="lg" onClick={() => navigate('/register/')}>
            Start for Free <ChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/blog/" className="hover:text-gray-900 transition">Blog</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
